import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@base/ui";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { Link, usarCaminho } from "../navegacao/navegacao";

export type SubitemDeNavegacao = {
  readonly titulo: string;
  readonly caminho: string;
};

export type ItemDeNavegacao = {
  readonly titulo: string;
  readonly icone: LucideIcon;
  /** Item com `caminho` e link; item com `subitens` abre um submenu. */
  readonly caminho?: string;
  readonly subitens?: readonly SubitemDeNavegacao[];
};

export type GrupoDeNavegacao = {
  readonly titulo: string;
  readonly itens: readonly ItemDeNavegacao[];
};

/**
 * Navegacao da barra lateral, em grupos (Projeto, Configuracoes, Gestao).
 *
 * Mesma estrutura do `nav-main` do bloco `sidebar-07`: item simples vira link;
 * item com subitens vira um submenu recolhivel, que ja abre quando a pagina
 * atual esta dentro dele. A pagina atual fica marcada e anunciada
 * (`aria-current`).
 */
export function NavMain({ grupos }: { readonly grupos: readonly GrupoDeNavegacao[] }) {
  const caminho = usarCaminho();

  return (
    <>
      {grupos.map((grupo) => (
        <SidebarGroup key={grupo.titulo}>
          <SidebarGroupLabel>{grupo.titulo}</SidebarGroupLabel>
          <SidebarMenu>
            {grupo.itens.map((item) =>
              item.subitens ? (
                <ItemComSubmenu key={item.titulo} item={item} caminho={caminho} />
              ) : (
                <SidebarMenuItem key={item.titulo}>
                  <SidebarMenuButton asChild isActive={caminho === item.caminho} tooltip={item.titulo}>
                    <Link
                      para={item.caminho ?? "/"}
                      aria-current={caminho === item.caminho ? "page" : undefined}
                    >
                      <item.icone aria-hidden="true" />
                      <span>{item.titulo}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ),
            )}
          </SidebarMenu>
        </SidebarGroup>
      ))}
    </>
  );
}

function ItemComSubmenu({ item, caminho }: { readonly item: ItemDeNavegacao; readonly caminho: string }) {
  const contemAtual = item.subitens?.some((subitem) => estaEm(caminho, subitem.caminho)) ?? false;

  return (
    <Collapsible asChild defaultOpen={contemAtual} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={item.titulo} isActive={contemAtual}>
            <item.icone aria-hidden="true" />
            <span>{item.titulo}</span>
            <ChevronRight
              aria-hidden="true"
              className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.subitens?.map((subitem) => (
              <SidebarMenuSubItem key={subitem.caminho}>
                <SidebarMenuSubButton asChild isActive={estaEm(caminho, subitem.caminho)}>
                  <Link
                    para={subitem.caminho}
                    aria-current={caminho === subitem.caminho ? "page" : undefined}
                  >
                    <span>{subitem.titulo}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

/** A pagina ou uma subpagina dela (ex.: Conexoes > Kommo CRM). */
function estaEm(caminho: string, destino: string): boolean {
  return caminho === destino || caminho.startsWith(`${destino}/`);
}
